import { replaceSpaceWithUnderscore } from '@dealstack/shared/lib/format/string';
import { Flex, MantineTheme, Menu, rem } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { useMemo } from 'react';

interface Option {
	label: string;
	value: string;
	icon?: React.ReactNode;
}

interface Props {
	optionsTitle?: string;
	options: Option[];
	action?: string;
	value: string;
	disabled?: boolean;
	placeholder?: string;
	onChange: (value: string) => void;
	onActionClick?: () => void;
	icon?: React.ReactNode;
	leftSection?: React.ReactNode;
	targetSx?: (theme: MantineTheme) => object;
	dataTestId?: string;
}

export const DropdownInput = ({
	optionsTitle,
	options,
	action,
	value,
	disabled,
	placeholder,
	onChange,
	onActionClick,
	icon,
	leftSection,
	targetSx,
	dataTestId,
}: Props) => {
	const label = useMemo(
		() => options.find((option) => option.value === value)?.label,
		[options, value]
	);

	return (
		<Menu shadow="md" width={200} position="bottom-start" disabled={disabled}>
			<Menu.Target>
				<Flex
					align="center"
					justify="space-between"
					w="100%"
					mt="0.5rem"
					px="0.5rem"
					onClick={(e) => {
						e.stopPropagation();
					}}
					data-testid={dataTestId}
					sx={(theme) => ({
						border: `1px solid ${disabled ? theme.colors.gray[8] : theme.colors.gray[4]}`,
						borderRadius: theme.radius.sm,
						cursor: disabled ? 'default' : 'pointer',
						backgroundColor: disabled ? theme.colors.gray[4] : '',
						color: disabled ? theme.colors.dark[0] : theme.colors.dark[8],
						height: rem('40px'),
						...(targetSx?.(theme) || {}),
					})}
				>
					<Flex align="center">
						{leftSection}
						{label || placeholder}
					</Flex>
					{icon ? (
						icon
					) : (
						<IconChevronDown
							size={40}
							stroke={1}
							color={disabled ? 'gray' : undefined}
						/>
					)}
				</Flex>
			</Menu.Target>

			<Menu.Dropdown
				sx={{
					padding: 0,
				}}
			>
				{optionsTitle && (
					<div
						onClick={(e) => {
							e.stopPropagation();
						}}
						className="font-bold px-3 py-2 bg-gray-100 mb-2"
					>
						{optionsTitle}
					</div>
				)}
				{options.map((option) => (
					<Menu.Item
						data-testid={replaceSpaceWithUnderscore(option.value)}
						key={option.value}
						onClick={(e) => {
							e.stopPropagation();
							onChange(option.value);
						}}
					>
						<div
							className={`text-sm py-2 px-3 hover:bg-gray-100 ${value === option.value ? 'bg-gray-200' : ''}`}
						>
							<Flex gap="0.5rem" align="center">
								{option.icon ? option.icon : ''}
								{option.label}
							</Flex>
						</div>
					</Menu.Item>
				))}

				{action && onActionClick && (
					<>
						<div
							className="mx-2"
							onClick={(e) => {
								e.stopPropagation();
							}}
						>
							<Menu.Divider />
						</div>
						<Menu.Item onClick={onActionClick}>
							<div
								className="text-sm py-2 px-3 mb-2 text-blue-600 hover:bg-blue-50"
								onClick={(e) => {
									e.stopPropagation();
								}}
							>
								{action}
							</div>
						</Menu.Item>
					</>
				)}
			</Menu.Dropdown>
		</Menu>
	);
};
